import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useReducer } from "react";
import { ArrowRight, ChevronRight, Globe } from "lucide-react";

import { Button, StatefulButton } from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { identitiesQueryKey } from "@/features/identities/queries";
import { submitNewDomain } from "@/features/identities/server-fns";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { cn } from "@/lib/utils";

import { WizardStepRail } from "./-wizard-rail";
import { StepDomain, StepPurpose, StepReview } from "./-wizard-steps";
import {
  getEffectiveDomain,
  initialWizardState,
  isStepValid,
  wizardReducer,
} from "./-wizard-state";

export const Route = createFileRoute("/dashboard/identities/domains/new/")({
  head: () => ({
    meta: [{ title: "New Domain - Bavimail" }],
  }),
  component: NewDomainPage,
});

/* ─── Step button labels ──────────────────────────────── */

const NEXT_LABELS: Record<number, string> = {
  1: "Continue",
  2: "Continue",
  3: "Review DNS Records",
};

/* ─── Page ────────────────────────────────────────────── */

function NewDomainPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);

  const canProceed = isStepValid(state, state.currentStep);

  /* ── Submit handler ──────────────────────────────────── */

  const handleSubmit = async () => {
    if (!isStepValid(state, 3)) return;

    try {
      dispatch({ type: "SET_SUBMIT_ERROR", payload: null });
      dispatch({ type: "SET_SUBMITTING", payload: true });

      const effectiveDomain = getEffectiveDomain(state);
      const created = await submitNewDomain({
        data: {
          domain: effectiveDomain,
          inboundEnabled: state.features.inboundMx,
        },
      });

      queryClient.setQueryData<{ domains: Array<typeof created> }>(
        identitiesQueryKey(),
        (current) => {
          if (!current) return { domains: [created] };
          const existingIndex = current.domains.findIndex((d) => d.id === created.id);
          if (existingIndex >= 0) {
            return {
              ...current,
              domains: current.domains.map((d) => (d.id === created.id ? created : d)),
            };
          }
          return { ...current, domains: [created, ...current.domains] };
        },
      );

      await queryClient.invalidateQueries({ queryKey: identitiesQueryKey() });

      capturePostHogEvent("domain_added", {
        domain: effectiveDomain,
        preset: state.purposePreset,
        features: Object.entries(state.features)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(","),
      });

      dispatch({ type: "SET_SUBMITTING", payload: false });
      await navigate({
        to: "/dashboard/identities",
        search: { domainId: created.id, setup: "choose" },
      });
    } catch (error) {
      dispatch({ type: "SET_SUBMITTING", payload: false });
      dispatch({
        type: "SET_SUBMIT_ERROR",
        payload: error instanceof Error ? error.message : "Failed to create the domain.",
      });
    }
  };

  /* ── Render ──────────────────────────────────────────── */

  return (
    <div className="flex h-full flex-col">
      {/* ── Header ── */}
      <div className="flex shrink-0 items-center border-b border-dashed border-hb-border px-6 py-4">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/identities"
            search={{ domainId: undefined, setup: undefined }}
            className="flex items-center gap-2 font-mono-hb text-xs text-hb-muted transition-colors hover:text-hb-accent"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            Identities
          </Link>
          <div className="h-4 w-px bg-hb-card" />
          <div className="flex items-center gap-3">
            <Globe className="h-4 w-4 text-hb-accent" />
            <h1 className="font-hb text-lg font-extrabold tracking-tight text-hb-heading">
              Add Domain
            </h1>
          </div>
        </div>
      </div>

      {/* ── Step rail ── */}
      <WizardStepRail
        currentStep={state.currentStep}
        furthestStep={state.furthestStep}
        onGoToStep={(step) => dispatch({ type: "GO_TO_STEP", payload: step })}
      />

      {/* ── Content area ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[720px] px-6 py-8 lg:px-8">
          <div
            key={`step-${state.currentStep}`}
            className={cn(
              state.stepDirection === "forward" ? "motion-panel-right" : "motion-panel-left",
            )}
          >
            {state.currentStep === 1 && <StepPurpose state={state} dispatch={dispatch} />}
            {state.currentStep === 2 && <StepDomain state={state} dispatch={dispatch} />}
            {state.currentStep === 3 && <StepReview state={state} dispatch={dispatch} />}
          </div>
        </div>
      </div>

      {/* ── Footer navigation ── */}
      <div className="flex shrink-0 items-center justify-between border-t border-dashed border-hb-border px-6 py-4">
        {/* Left: Back or Cancel */}
        {state.currentStep === 1 ? (
          <ButtonRouterLink
            to="/dashboard/identities"
            search={{ domainId: undefined, setup: undefined }}
            variant="secondary"
            shape="sharp"
          >
            Cancel
          </ButtonRouterLink>
        ) : (
          <Button
            type="button"
            variant="secondary"
            shape="sharp"
            onClick={() => dispatch({ type: "PREV_STEP" })}
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            Back
          </Button>
        )}

        {/* Right: Next or Submit */}
        {state.currentStep < 3 ? (
          <Button
            type="button"
            shape="sharp"
            disabled={!canProceed}
            onClick={() => dispatch({ type: "NEXT_STEP" })}
          >
            {NEXT_LABELS[state.currentStep] ?? "Continue"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <StatefulButton
            type="button"
            shape="sharp"
            state={state.submitting ? "loading" : "idle"}
            loadingText="Initializing"
            icon={<ArrowRight className="h-4 w-4" />}
            disabled={!canProceed}
            onClick={() => void handleSubmit()}
          >
            Add Domain & Configure DNS
          </StatefulButton>
        )}
      </div>
    </div>
  );
}
